/**
 * Researcher API Service
 *
 * API functions for researcher role functionality.
 */

import apiClient from './api';

// ============ Budget Operations ============

/**
 * Get current budget status
 */
export const getBudgetStatus = async () => {
  try {
    const response = await apiClient.get('/researcher/budget');
    return response.data;
  } catch (error) {
    console.error('Error getting budget status:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get budget usage history
 * @param {Object} options - Query options
 */
export const getBudgetHistory = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await apiClient.get(`/researcher/budget/history?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error getting budget history:', error);
    throw error.response?.data || error;
  }
};

// ============ Sandbox Persona Operations ============

/**
 * List sandbox personas (owned and shared)
 * @param {string} personaType - Optional filter by 'client' or 'therapist'
 */
export const listSandboxPersonas = async (personaType = null) => {
  try {
    const params = personaType ? `?personaType=${personaType}` : '';
    const response = await apiClient.get(`/researcher/sandbox/personas${params}`);
    return response.data;
  } catch (error) {
    console.error('Error listing sandbox personas:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get a specific sandbox persona
 * @param {string} personaId - Persona ID
 */
export const getSandboxPersona = async (personaId) => {
  try {
    const response = await apiClient.get(`/researcher/sandbox/personas/${personaId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting sandbox persona:', error);
    throw error.response?.data || error;
  }
};

/**
 * Create a new sandbox persona
 * @param {Object} personaData - Persona data
 */
export const createSandboxPersona = async (personaData) => {
  try {
    const response = await apiClient.post('/researcher/sandbox/personas', personaData);
    return response.data;
  } catch (error) {
    console.error('Error creating sandbox persona:', error);
    throw error.response?.data || error;
  }
};

/**
 * Clone a live persona to sandbox
 * @param {string} personaType - 'client' or 'therapist'
 * @param {string} livePersonaKey - Key of live persona to clone
 * @param {string} newKey - Optional new key
 */
export const cloneFromLive = async (personaType, livePersonaKey, newKey = null) => {
  try {
    const response = await apiClient.post('/researcher/sandbox/personas/clone-from-live', {
      personaType,
      livePersonaKey,
      newKey
    });
    return response.data;
  } catch (error) {
    console.error('Error cloning live persona:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update a sandbox persona
 * @param {string} personaId - Persona ID
 * @param {Object} updateData - Fields to update
 */
export const updateSandboxPersona = async (personaId, updateData) => {
  try {
    const response = await apiClient.put(`/researcher/sandbox/personas/${personaId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating sandbox persona:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete a sandbox persona
 * @param {string} personaId - Persona ID
 */
export const deleteSandboxPersona = async (personaId) => {
  try {
    const response = await apiClient.delete(`/researcher/sandbox/personas/${personaId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting sandbox persona:', error);
    throw error.response?.data || error;
  }
};

// ============ Sharing Operations ============

/**
 * Share a sandbox persona with another researcher
 * @param {string} personaId - Persona ID
 * @param {string} targetUserId - User to share with
 * @param {string} permission - 'view', 'edit', or 'full'
 */
export const sharePersona = async (personaId, targetUserId, permission = 'view') => {
  try {
    const response = await apiClient.post(`/researcher/sandbox/personas/${personaId}/share`, {
      targetUserId,
      permission
    });
    return response.data;
  } catch (error) {
    console.error('Error sharing persona:', error);
    throw error.response?.data || error;
  }
};

/**
 * Unshare a sandbox persona
 * @param {string} personaId - Persona ID
 * @param {string} userId - User to unshare from
 */
export const unsharePersona = async (personaId, userId) => {
  try {
    const response = await apiClient.delete(`/researcher/sandbox/personas/${personaId}/share/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error unsharing persona:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get personas shared with current user
 */
export const getSharedWithMe = async () => {
  try {
    const response = await apiClient.get('/researcher/sandbox/shared-with-me');
    return response.data;
  } catch (error) {
    console.error('Error getting shared personas:', error);
    throw error.response?.data || error;
  }
};

// ============ Live Persona Access (Read-Only) ============

/**
 * Get live client personas (read-only)
 */
export const getLiveClientPersonas = async () => {
  try {
    const response = await apiClient.get('/researcher/personas/client');
    return response.data;
  } catch (error) {
    console.error('Error getting live client personas:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get live therapist personas (read-only)
 */
export const getLiveTherapistPersonas = async () => {
  try {
    const response = await apiClient.get('/researcher/personas/therapist');
    return response.data;
  } catch (error) {
    console.error('Error getting live therapist personas:', error);
    throw error.response?.data || error;
  }
};

// ============ Utility Operations ============

/**
 * Get list of other researchers (for sharing)
 */
export const getOtherResearchers = async () => {
  try {
    const response = await apiClient.get('/researcher/researchers');
    return response.data;
  } catch (error) {
    console.error('Error getting researchers:', error);
    throw error.response?.data || error;
  }
};

// ============ Admin Operations ============

/**
 * List all researchers (admin only)
 */
export const listResearchers = async () => {
  try {
    const response = await apiClient.get('/admin/researchers');
    return response.data;
  } catch (error) {
    console.error('Error listing researchers:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get researcher statistics (admin only)
 */
export const getResearcherStats = async () => {
  try {
    const response = await apiClient.get('/admin/researchers/stats');
    return response.data;
  } catch (error) {
    console.error('Error getting researcher stats:', error);
    throw error.response?.data || error;
  }
};

/**
 * Grant researcher role to a user (admin only)
 * @param {string} userId - User ID
 * @param {Object} options - Role options
 */
export const grantResearcherRole = async (userId, options = {}) => {
  try {
    const response = await apiClient.post(`/admin/researchers/${userId}/grant-role`, options);
    return response.data;
  } catch (error) {
    console.error('Error granting researcher role:', error);
    throw error.response?.data || error;
  }
};

/**
 * Revoke researcher role from a user (admin only)
 * @param {string} userId - User ID
 */
export const revokeResearcherRole = async (userId) => {
  try {
    const response = await apiClient.post(`/admin/researchers/${userId}/revoke-role`);
    return response.data;
  } catch (error) {
    console.error('Error revoking researcher role:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update budget limits for a researcher (admin only)
 * @param {string} userId - User ID
 * @param {Object} limits - { dailyLimit, monthlyLimit }
 */
export const updateResearcherBudget = async (userId, limits) => {
  try {
    const response = await apiClient.put(`/admin/researchers/${userId}/budget`, limits);
    return response.data;
  } catch (error) {
    console.error('Error updating researcher budget:', error);
    throw error.response?.data || error;
  }
};

/**
 * Refresh/reset budget for a researcher (admin only)
 * @param {string} userId - User ID
 * @param {Object} options - Reset options
 */
export const refreshResearcherBudget = async (userId, options = {}) => {
  try {
    const response = await apiClient.post(`/admin/researchers/${userId}/budget/refresh`, options);
    return response.data;
  } catch (error) {
    console.error('Error refreshing researcher budget:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get budget history for a researcher (admin only)
 * @param {string} userId - User ID
 * @param {Object} options - Query options
 */
export const getResearcherBudgetHistory = async (userId, options = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await apiClient.get(`/admin/researchers/${userId}/budget/history?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error getting researcher budget history:', error);
    throw error.response?.data || error;
  }
};

/**
 * List all sandbox personas (admin only)
 * @param {Object} options - Filter options
 */
export const listAllSandboxPersonas = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await apiClient.get(`/admin/researchers/sandbox/personas?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error listing all sandbox personas:', error);
    throw error.response?.data || error;
  }
};

/**
 * Promote sandbox persona to live (admin only)
 * @param {string} personaId - Persona ID
 * @param {Object} options - { newKey, newName }
 */
export const promoteSandboxToLive = async (personaId, options = {}) => {
  try {
    const response = await apiClient.post(`/admin/researchers/sandbox/personas/${personaId}/promote`, options);
    return response.data;
  } catch (error) {
    console.error('Error promoting sandbox persona:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get all simulation runs (admin only)
 * @param {Object} options - Filter options
 */
export const getAllSimulationRuns = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await apiClient.get(`/admin/researchers/simulation/all-runs?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error getting all simulation runs:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update RL Loop access for a researcher (admin only)
 * @param {string} userId - User ID
 * @param {boolean} canAccessRLLoop - Whether to grant or revoke access
 */
export const updateRLLoopAccess = async (userId, canAccessRLLoop) => {
  try {
    const response = await apiClient.put(`/admin/researchers/${userId}/rl-loop-access`, { canAccessRLLoop });
    return response.data;
  } catch (error) {
    console.error('Error updating RL Loop access:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update researcher settings (admin only)
 * @param {string} userId - User ID
 * @param {Object} settings - Settings to update
 */
export const updateResearcherSettings = async (userId, settings) => {
  try {
    const response = await apiClient.put(`/admin/researchers/${userId}/settings`, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating researcher settings:', error);
    throw error.response?.data || error;
  }
};

export default {
  // Budget
  getBudgetStatus,
  getBudgetHistory,
  // Sandbox Personas
  listSandboxPersonas,
  getSandboxPersona,
  createSandboxPersona,
  cloneFromLive,
  updateSandboxPersona,
  deleteSandboxPersona,
  // Sharing
  sharePersona,
  unsharePersona,
  getSharedWithMe,
  // Live Personas
  getLiveClientPersonas,
  getLiveTherapistPersonas,
  // Utility
  getOtherResearchers,
  // Admin
  listResearchers,
  getResearcherStats,
  grantResearcherRole,
  revokeResearcherRole,
  updateResearcherBudget,
  refreshResearcherBudget,
  getResearcherBudgetHistory,
  listAllSandboxPersonas,
  promoteSandboxToLive,
  getAllSimulationRuns,
  updateRLLoopAccess,
  updateResearcherSettings
};
